import React, { useEffect, useState } from 'react';
import PageContentWrapper from '../../components/PageContentWrapper/PageContentWrapper';
import CardContainer from '../../components/CardContainer/CardContainer';
import { clsx } from 'clsx';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ProductCard } from 'src/components/HomePageComponents/ProductCard';
import { useGetAllProjectsQuery, useGetProjectsStatisticsSummaryQuery } from 'src/services';
import { CreateNewAgentCardModal } from 'src/components/HomePageComponents/CreateNewAgent/CreateNewAgentCardModal';
import { useLoader } from 'src/provider/LoaderProvider';
import moment from 'moment';
import { API_VERSION } from 'src/constants/api-routes';

const HomePage = () => {
  const isLgDown = useMediaQuery('(max-width: 992px)');
  const loader = useLoader();
  const [projWithStatistics, setProjWithStatistics] = useState({ agent: [], bi: [], customer: [] });
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV2 = version === API_VERSION.V2;
  const { data: projects, isFetching, isSuccess } = useGetAllProjectsQuery();

  const projectsByType = projects?.reduce(
    (acc, project) => {
      if (!acc[project?.project_type]) {
        acc[project.project_type] = [];
      }
      acc[project?.project_type].push(project);
      return acc;
    },
    { agent: [], bi: [], customer: [] },
  );

  const { data: statisticData, isFetching: isFetchingStat } = (
    useGetProjectsStatisticsSummaryQuery as any
  )(
    {
      start_date: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss.000'),
      end_date: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss.000'),
      cocustomer_project_ids: projectsByType?.customer
        ?.map((item) => `cocustomer_project_ids=${item?.id}`)
        .join('&'),
      coagent_project_ids: projectsByType?.agent
        ?.map((item) => `coagent_project_ids=${item?.id}`)
        .join('&'),
    },
    {
      skip:
        isV2 ||
        isFetching ||
        (projectsByType && !Object.values(projectsByType).some((arr: any) => arr.length > 0)),
    },
  );

  useEffect(() => {
    if (isFetching) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isFetching]);

  useEffect(() => {
    if (projects) {
      if (statisticData) {
        const mergedCustomer = projectsByType.customer.map((project) => {
          const stat = statisticData?.cocustomer?.find((proj) => proj.project_id === project.id);
          return stat ? { ...project, ...stat } : project;
        });

        const mergedAgent = projectsByType.agent.map((project) => {
          const stat = statisticData?.coagent?.find((proj) => proj.project_id === project.id);
          return stat ? { ...project, ...stat } : project;
        });

        const mergedProjects = {
          ...projectsByType,
          customer: mergedCustomer,
          agent: mergedAgent,
        };
        setProjWithStatistics(mergedProjects);
      } else {
        setProjWithStatistics(projectsByType);
      }
    }
  }, [projects, isSuccess, statisticData]);

  return (
    <React.Fragment>
      <PageContentWrapper
        className={clsx('h-full', !isLgDown && 'relative')}
        isMobileMarginTop={false}
        isProjectBreadcrumbs={true}
        projectBreadcrumbsContent={'Knowledge Base'}
      >
        <div className="z-10 gap-[10px] flex flex-col relative h-[calc(100vh-130px)] overflow-hidden">
          <CardContainer className="gap-[16px] h-full max-h-full !p-[15px] !shadow-none">
            <div
              className={clsx(
                'h-full max-h-full overflow-auto p-0.5 grid grid-cols-3 gap-2.5',
                isFetching && 'animate-pulse blur-sm pointer-events-none',
              )}
            >
              {[
                ...projWithStatistics.customer,
                ...projWithStatistics.agent,
                ...projWithStatistics.bi,
              ].map((project) => {
                return (
                  <ProductCard
                    key={`${project?.project_type || 'project'}-${project.id}`}
                    project={project}
                    isFetchingStat={isFetchingStat}
                  />
                );
              })}
              <CreateNewAgentCardModal />
            </div>
          </CardContainer>
        </div>
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default HomePage;
